import { useCallback, useMemo, useState } from 'react';
import {
  isBlockedNumberAlreadyBlockedError,
  isBlockedNumberAlreadyUnblockedError,
  normalizeNumberForBlocking,
  useBlockedNumberLookup,
  useCreateBlockedNumberMutation,
  useDeleteBlockedNumberMutation,
} from '@frontend/api-call-block';
import { useTranslation } from '@frontend/i18n';
import { useAppScopeStore } from '@frontend/scope';
import { useAlert } from '@frontend/design-system';
import { BlockNumberModalMode, BlockNumberModalProps } from './block-number-modal';

type BlockTarget = {
  mode: BlockNumberModalMode;
  phoneNumber: string;
  tenantId: string;
};

/**
 * Owns the block/unblock confirmation flow for phone numbers: tenant resolution, the confirmation modal state,
 * the mutations, alerts and undo. Pass the returned modalProps to a BlockNumberModal.
 */
export const useBlockNumberFlow = ({ locationIds }: { locationIds: string[] }) => {
  const { t } = useTranslation('calls');
  const alert = useAlert();
  const { accessibleLocationData } = useAppScopeStore();

  // The target outlives closing so the modal keeps its content while it animates out.
  const [target, setTarget] = useState<BlockTarget | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const getTenantId = useCallback(
    (locationId?: string) => (locationId ? accessibleLocationData[locationId]?.phoneTenantId : undefined),
    [accessibleLocationData]
  );

  const tenantIds = useMemo(() => {
    const ids = new Set<string>();
    locationIds.forEach((locationId) => {
      const tenantId = getTenantId(locationId);
      if (tenantId) ids.add(tenantId);
    });
    return Array.from(ids);
  }, [locationIds, getTenantId]);

  const { isNumberBlocked, getBlockedNumber, isError } = useBlockedNumberLookup({ tenantIds });
  const { mutateAsync: createBlockedNumber } = useCreateBlockedNumberMutation({ optimisticUpdate: true });
  const { mutateAsync: deleteBlockedNumber } = useDeleteBlockedNumberMutation({ optimisticUpdate: true });

  const isBlocked = useCallback(
    (phoneNumber?: string | number, locationId?: string) => isNumberBlocked(getTenantId(locationId), phoneNumber),
    [getTenantId, isNumberBlocked]
  );

  const requestBlock = useCallback(
    (phoneNumber: string, locationId?: string) => {
      const tenantId = getTenantId(locationId);
      if (!tenantId) {
        alert.error(t('Failed to block number: No tenant found for this location.'));
        return;
      }
      setTarget({ mode: 'block', phoneNumber, tenantId });
      setIsOpen(true);
    },
    [alert, getTenantId, t]
  );

  const requestUnblock = useCallback(
    (phoneNumber: string, locationId?: string) => {
      const tenantId = getTenantId(locationId);
      if (!tenantId) {
        alert.error(t('Failed to unblock number: No tenant found for this location.'));
        return;
      }
      setTarget({ mode: 'unblock', phoneNumber, tenantId });
      setIsOpen(true);
    },
    [alert, getTenantId, t]
  );

  const unblock = useCallback(
    async (tenantId: string, blockedNumberId: string) => {
      try {
        await deleteBlockedNumber({ id: blockedNumberId, _otherOptions: { tenantId } });
        alert.success(t('Phone number unblocked successfully.'));
      } catch (error) {
        alert.error(
          isBlockedNumberAlreadyUnblockedError(error)
            ? t('Phone number already unblocked.')
            : t('Failed to unblock number.')
        );
      }
    },
    [alert, deleteBlockedNumber, t]
  );

  const block = useCallback(
    async (tenantId: string, phoneNumber: string) => {
      try {
        const response = await createBlockedNumber({
          tenantId,
          phoneNumber: {
            countryCode: 1,
            nationalNumber: Number(normalizeNumberForBlocking(phoneNumber)) || undefined,
          },
        });

        const createdId = response?.blockedNumber?.id;
        alert.success({
          message: t('Phone number blocked successfully.'),
          action: createdId ? { label: t('Undo'), onClick: () => unblock(tenantId, createdId) } : undefined,
        });
      } catch (error) {
        alert.error(
          isBlockedNumberAlreadyBlockedError(error) ? t('Phone number already blocked.') : t('Failed to block number.')
        );
      }
    },
    [alert, createBlockedNumber, t, unblock]
  );

  const onConfirm = useCallback(async () => {
    if (!target) return;
    const { mode, phoneNumber, tenantId } = target;

    if (mode === 'block') {
      await block(tenantId, phoneNumber);
    } else {
      // Resolved at confirm time so a temp id from an in-flight optimistic add is never used.
      const blockedNumberId = getBlockedNumber(tenantId, phoneNumber)?.id;
      if (blockedNumberId) {
        await unblock(tenantId, blockedNumberId);
      } else {
        alert.error(t('Failed to unblock number: Blocked record not found.'));
      }
    }

    setIsOpen(false);
  }, [alert, block, getBlockedNumber, t, target, unblock]);

  const onClose = useCallback(() => setIsOpen(false), []);

  const modalProps: BlockNumberModalProps = {
    show: isOpen,
    mode: target?.mode ?? 'block',
    phoneNumber: target?.phoneNumber ?? '',
    onClose,
    onConfirm,
  };

  return {
    isBlocked,
    requestBlock,
    requestUnblock,
    modalProps,
    // A query with no data resets to loading when it refetches, so this is never true while loading.
    isCallBlockingFeatureUnavailable: isError,
  };
};
